import { useState } from "react";
import { Lock, PhoneCall, MessageCircle } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { SITE, DEFAULT_RATES, inr, waLink, RATES_COMING_SOON, RATES_SOON_LABEL } from "@/lib/site";
import { useFeedback } from "@/components/Feedback";

const KARATS = [
  { key: "gold_24k", label: "24K · 999" },
  { key: "gold_22k", label: "22K · 916" },
  { key: "gold_18k", label: "18K · 750" },
];

export const Rates = ({ rates }) => {
  const r = rates || DEFAULT_RATES;
  const [karat, setKarat] = useState("gold_22k");
  const [grams, setGrams] = useState("10");
  const { openEnquiry } = useFeedback();

  const g = parseFloat(grams) || 0;
  const value = r[karat] * g;
  const making = value * 0.12;
  const gst = (value + making) * 0.03;
  const total = value + making + gst;
  const karatLabel = KARATS.find((k) => k.key === karat).label;

  const cards = [
    { label: "24K · 999 Pure", value: r.gold_24k, testid: "rate-card-24k" },
    { label: "22K · 916 Hallmark", value: r.gold_22k, testid: "rate-card-22k" },
    { label: "18K · Diamond Gold", value: r.gold_18k, testid: "rate-card-18k" },
    { label: "Silver · 999", value: r.silver, testid: "rate-card-silver" },
  ];

  return (
    <section id="rates" data-testid="rates-section" className="relative border-y border-[#D4AF37]/10 bg-[#081022] py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <SectionHead
          eyebrow={RATES_COMING_SOON ? "Board Rates" : "Live Bullion Pulse"}
          title={<>Today's gold, <span className="gold-text italic">priced honestly.</span></>}
          sub={
            RATES_COMING_SOON
              ? "Our daily board rates will appear here shortly. Until then, call or WhatsApp us for the exact rate of the day — no mark-ups, no hidden charges."
              : "Board rates refreshed every morning. Estimate your ornament below — weight, making and GST, all itemised."
          }
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((c, i) => (
            <Reveal key={c.label} delay={i * 0.07}>
              <div data-testid={c.testid} className="glass-card rounded-2xl p-6 transition-colors duration-500 hover:border-[#D4AF37]/50">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#79756E]">{c.label}</p>
                {RATES_COMING_SOON ? (
                  <p className="mt-3 font-mono text-lg text-[#E6C265]">{RATES_SOON_LABEL}</p>
                ) : (
                  <p className="mt-3 font-mono text-3xl text-[#E6C265]">
                    {inr(c.value)}
                    <span className="text-sm text-[#79756E]">/g</span>
                  </p>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        {RATES_COMING_SOON ? (
          <Reveal delay={0.15} className="mt-12">
            <div data-testid="rates-coming-soon-panel" className="glass-card flex flex-col items-center gap-6 rounded-3xl p-10 text-center lg:p-14">
              <p className="eyebrow">Rate of the day</p>
              <h3 className="max-w-2xl font-serif text-2xl text-white sm:text-3xl">
                Live board rates are <span className="gold-text italic">coming soon</span> to this page.
              </h3>
              <p className="max-w-xl text-sm font-light text-[#B5B0A8]">
                Ask us for today's 916 gold and silver rate — we quote the same rate on the phone as on our showroom board.
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <a data-testid="rates-soon-call-button" href={`tel:${SITE.phoneRaw}`} className="btn-gold !py-3">
                  <PhoneCall size={15} /> Call {SITE.phoneDisplay}
                </a>
                <a
                  data-testid="rates-soon-whatsapp-button"
                  href={waLink("Namaste Aradhana Jewellers! Please share today's 916 gold rate.")}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost !py-3"
                >
                  <MessageCircle size={15} /> Ask on WhatsApp
                </a>
              </div>
            </div>
          </Reveal>
        ) : (
        <Reveal delay={0.15} className="mt-12">
          <div className="glass-card grid gap-10 rounded-3xl p-8 lg:grid-cols-2 lg:p-12">
            <div>
              <h3 className="font-serif text-2xl text-white sm:text-3xl">Ornament Estimator</h3>
              <p className="mt-2 text-sm font-light text-[#B5B0A8]">Choose a purity and enter the weight in grams.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                {KARATS.map((k) => (
                  <button
                    key={k.key}
                    data-testid={`calculator-karat-${k.key}`}
                    onClick={() => setKarat(k.key)}
                    className={`rounded-full px-5 py-2.5 font-mono text-xs tracking-wider transition-all duration-300 ${
                      karat === k.key
                        ? "bg-[#D4AF37] text-black"
                        : "border border-white/10 text-[#B5B0A8] hover:border-[#D4AF37]/40"
                    }`}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
              <label className="mt-8 block">
                <span className="eyebrow">Weight (grams)</span>
                <input
                  data-testid="gold-calculator-grams-input"
                  type="number"
                  min="0"
                  value={grams}
                  onChange={(e) => setGrams(e.target.value)}
                  className="lux-input mt-3 font-mono text-lg"
                />
              </label>
            </div>
            <div className="flex flex-col justify-between rounded-2xl border border-white/5 bg-[#060B18]/60 p-7">
              <div className="space-y-4 font-mono text-sm">
                <div className="flex justify-between text-[#B5B0A8]">
                  <span>Gold value · {karatLabel} × {g}g</span>
                  <span data-testid="calculator-gold-value" className="text-white">{inr(value)}</span>
                </div>
                <div className="flex justify-between text-[#B5B0A8]">
                  <span>Making charges · est. 12%</span>
                  <span data-testid="calculator-making-value" className="text-white">{inr(making)}</span>
                </div>
                <div className="flex justify-between text-[#B5B0A8]">
                  <span>GST · 3%</span>
                  <span data-testid="calculator-gst-value" className="text-white">{inr(gst)}</span>
                </div>
                <div className="border-t border-[#D4AF37]/20 pt-4">
                  <div className="flex items-end justify-between">
                    <span className="text-[#99732B]">Estimated total</span>
                    <span data-testid="calculator-total-value" className="font-mono text-3xl text-[#E6C265]">{inr(total)}</span>
                  </div>
                </div>
              </div>
              <button
                data-testid="lock-rate-button"
                onClick={() => openEnquiry(`Rate lock — ${karatLabel} · ${g}g @ ${inr(r[karat])}/g`, "rate-lock")}
                className="btn-gold mt-8 w-full !py-3"
              >
                <Lock size={15} /> Lock this rate for 48 hours
              </button>
              <p className="mt-3 text-center text-[11px] font-light text-[#79756E]">
                Indicative estimate — final invoice at the showroom with zero hidden charges.
              </p>
            </div>
          </div>
        </Reveal>
        )}
      </div>
    </section>
  );
};
